// Cave84 — Firestore plumbing (Phase 0 Day 4)
// Shared converter factory and error helpers used by every service module.
//
// Why a converter factory:
//   Every entity in shared-types/index.ts has the shape `{ id: string, ...rest }`.
//   Firestore stores everything under `rest` (the doc id is implicit in the
//   doc path). We don't want each service to repeat the same `(snap) => ({
//   id: snap.id, ...snap.data() })` boilerplate.
//
// Convention:
//   - `id` is NEVER written to the document body.
//   - `created_at` / `updated_at` / `last_login` etc. are owned by the caller
//     and use `serverTimestamp()` from firebase/firestore directly.

import {
  collection,
  doc,
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
  type FirestoreDataConverter,
  type FirestoreError,
  type QueryDocumentSnapshot,
  type SnapshotOptions
} from 'firebase/firestore';
import { db } from './firebase';

// ---------------------------------------------------------------------------
// Converter factory
// ---------------------------------------------------------------------------

/**
 * Build a typed converter for any entity that has the shape `{ id: string, ...T }`.
 * The `id` field is stripped on write (Firestore doc id holds it) and stitched
 * back on read.
 */
export function entityConverter<T extends { id: string }>(): FirestoreDataConverter<T> {
  return {
    toFirestore(data) {
      // Strip `id` — Firestore stores it in the doc path, not the body.
      const { id: _id, ...rest } = data as T;
      return rest as DocumentData;
    },
    fromFirestore(snap: QueryDocumentSnapshot, options?: SnapshotOptions): T {
      const data = snap.data(options);
      return { id: snap.id, ...(data as Omit<T, 'id'>) } as T;
    }
  };
}

/**
 * Typed collection reference. Wraps `collection(db, name).withConverter(...)`
 * so callers can write `collection<Cellar>('cellars')` once and reuse it.
 */
export function typedCollection<T extends { id: string }>(name: string): CollectionReference<T> {
  return collection(db, name).withConverter(entityConverter<T>());
}

/**
 * Typed document reference under a typed collection.
 */
export function typedDoc<T extends { id: string }>(
  name: string,
  id: string
): DocumentReference<T> {
  return doc(db, name, id).withConverter(entityConverter<T>());
}

// ---------------------------------------------------------------------------
// Error normalization
// ---------------------------------------------------------------------------

/**
 * Translate a FirestoreError into a short, human-readable string for UI banners.
 * Returns null for non-Firestore errors (caller decides whether to rethrow).
 */
export function describeFirestoreError(err: unknown): string | null {
  if (!isFirestoreError(err)) return null;
  switch (err.code) {
    case 'permission-denied':
      return 'You do not have permission to perform this action.';
    case 'unavailable':
      return 'Firestore is unreachable. Check your connection.';
    case 'not-found':
      return 'The requested document does not exist.';
    case 'failed-precondition':
      return 'Operation rejected by Firestore (failed precondition).';
    case 'unauthenticated':
      return 'Your session has expired. Please sign in again.';
    default:
      return `Firestore error (${err.code}): ${err.message}`;
  }
}

function isFirestoreError(err: unknown): err is FirestoreError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    typeof (err as { code: unknown }).code === 'string'
  );
}

// ---------------------------------------------------------------------------
// Query-key conventions (TanStack Query)
// ---------------------------------------------------------------------------

/**
 * Single source of truth for query keys. Keeps invalidation paths stable when
 * services mutate data: e.g., creating a cellar invalidates `qk.cellars.all`.
 */
export const qk = {
  cellars: {
    all: ['cellars'] as const,
    byId: (id: string) => ['cellars', id] as const
  },
  locations: {
    all: ['locations'] as const,
    byCellar: (cellarId: string) => ['locations', { cellarId }] as const
  },
  producers: {
    all: ['producers'] as const,
    byId: (id: string) => ['producers', id] as const
  },
  appellations: {
    all: ['appellations'] as const,
    byId: (id: string) => ['appellations', id] as const
  },
  wines: {
    all: ['wines'] as const,
    byId: (id: string) => ['wines', id] as const,
    byProducer: (producerId: string) => ['wines', { producerId }] as const
  },
  vintages: {
    all: ['vintages'] as const,
    byWine: (wineId: string) => ['vintages', { wineId }] as const,
    byId: (id: string) => ['vintages', id] as const
  },
  criticScores: {
    byVintage: (vintageId: string) => ['criticScores', { vintageId }] as const
  },
  bottles: {
    all: ['bottles'] as const,
    byCellar: (cellarId: string) => ['bottles', { cellarId }] as const,
    byWine: (wineId: string) => ['bottles', { wineId }] as const,
    byVintageIds: (vintageIds: string[]) =>
      ['bottles', { vintageIds: [...vintageIds].sort().join(',') }] as const,
    byId: (id: string) => ['bottles', id] as const
  },
  marketPrices: {
    byVintage: (vintageId: string) => ['marketPrices', { vintageId }] as const,
    latestByVintage: (vintageId: string) => ['marketPrices', 'latest', { vintageId }] as const,
  },
  wishlist: {
    all: ['wishlist'] as const,
    byId: (id: string) => ['wishlist', id] as const,
    byWine: (wineId: string) => ['wishlist', { wineId }] as const,
  },
  vintageCharts: {
    all: ['vintageCharts'] as const,
    byRegion: (region: string) => ['vintageCharts', { region }] as const,
    byId: (id: string) => ['vintageCharts', id] as const,
    byRegionYear: (region: string, year: number) =>
      ['vintageCharts', { region, year }] as const
  },
  consumption: {
    byBottle: (bottleId: string) => ['consumption', { bottleId }] as const
  }
} as const;
